import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import cloudStorageService from '../../../services/cloudStorageService';

const StorageConfigPanel = ({ metrics, onRefresh }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await cloudStorageService?.getClientStorageBreakdown();
      setClients(data);
    } catch (err) {
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Storage Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <Icon name="HardDrive" size={24} className="text-blue-600" />
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">Total Storage Used</p>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {metrics?.totalStorageGB?.toFixed(2) || 0} GB
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={24} className="text-green-600" />
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">Active Clients</p>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                {metrics?.activeClients || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={24} className="text-amber-600" />
            <div>
              <p className="text-sm text-amber-700 dark:text-amber-300">Total Files</p>
              <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                {metrics?.totalFiles?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Storage Breakdown */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Icon name="Building" size={20} />
            Client Storage Segregation
          </h3>
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadClients}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader" size={24} className="animate-spin text-blue-600 mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Storage Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Files
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Encryption
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Compliance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {clients?.map(client => (
                  <tr key={client?.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {client?.companyName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {client?.storageGB?.toFixed(2)} GB
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {client?.fileCount?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        client?.encryptionEnabled
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      }`}>
                        <Icon name={client?.encryptionEnabled ? 'Lock' : 'Unlock'} size={12} />
                        {client?.encryptionEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        client?.complianceStatus === 'compliant' ?'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                      }`}>
                        <Icon name="Shield" size={12} />
                        {client?.complianceStatus === 'compliant' ? 'Compliant' : 'Review Required'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StorageConfigPanel;